import { useChannel } from "ably/react";
import Dropdown from "components/common/Dropdown";
import TextInput from "components/common/InputField/TextInput";
import TaskCard from "components/common/TaskCard";
import { camelCaseToRegularCase } from "helpers/camelCaseToRegularCase";
import { agentTypeRename } from "helpers/renameAgentType";
import useGetTaskList from "hooks/workforceHooks/useGetTaskList";
import { orderBy, truncate } from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

type FlowAllTaskProps = {
    filteredList: Record<string, any>[];
    profile: Record<string, any>;
};

const FlowAllTask = ({ filteredList, profile }: FlowAllTaskProps) => {
    const navigate = useNavigate();
    const [allTasks, setAllTasks] = useState([]);

    const {
        data: tasks,
        search: searchTask,
        handleSearch,
        handleDebouncedChange,
        handleTaskStatusChange,
        handleClearTaskStatusFilter,
        handleTaskTypeChange,
        handleClearTaskTypeFilter,
        taskStatus,
        taskType,
        pagination,
        setUpdatedPage,
        setLoadMore,
    } = useGetTaskList();

    const {} = useChannel(
        `agent_tasks_${profile?.user?.parentId || profile?.user?._id}`,
        (taskData) => {
            const isTask = taskData?.name?.split("_")?.length === 2;

            if (isTask) {
                let activeTasksCopy = [...orderBy(tasks?.activeTasks || [], "createdAt", ["desc"])];
                let pendingTasksCopy = [
                    ...orderBy(tasks?.pendingTasks || [], "createdAt", ["desc"]),
                ];
                let completedTasksCopy = [
                    ...orderBy(tasks?.completedTasks || [], "createdAt", ["desc"]),
                ];
                let failedTasksCopy = [...orderBy(tasks?.failedTasks || [], "createdAt", ["desc"])];

                const activeTaskIndex = activeTasksCopy?.findIndex(
                    (task) => task?._id === taskData?.data?.task?._id
                );
                const pendingTaskIndex = pendingTasksCopy?.findIndex(
                    (task) => task?._id === taskData?.data?.task?._id
                );
                const completedTaskIndex = pendingTasksCopy?.findIndex(
                    (task) => task?._id === taskData?.data?.task?._id
                );
                const failedTaskIndex = failedTasksCopy?.findIndex(
                    (task) => task?._id === taskData?.data?.task?._id
                );

                const moveToPending =
                    taskData?.data?.task?.status?.toLowerCase() === "pending" ||
                    taskData?.data?.task?.status?.toLowerCase() === "attention required";
                const moveToActive = taskData?.data?.task?.status?.toLowerCase() === "in progress";
                const moveToCompleted = taskData?.data?.task?.status?.toLowerCase() === "completed";
                const moveToFailed = taskData?.data?.task?.status?.toLowerCase() === "failed";

                if (activeTaskIndex !== -1) {
                    activeTasksCopy?.splice(activeTaskIndex, 1);
                }

                if (pendingTaskIndex !== -1) {
                    pendingTasksCopy?.splice(pendingTaskIndex, 1);
                }

                if (completedTaskIndex !== -1) {
                    completedTasksCopy?.splice(completedTaskIndex, 1);
                }

                if (failedTaskIndex !== -1) {
                    failedTasksCopy?.splice(failedTaskIndex, 1);
                }

                if (moveToPending) {
                    pendingTasksCopy = [taskData?.data?.task, ...pendingTasksCopy];
                }
                if (moveToActive) {
                    activeTasksCopy = [taskData?.data?.task, ...activeTasksCopy];
                }
                if (moveToCompleted) {
                    completedTasksCopy = [taskData?.data?.task, ...completedTasksCopy];
                }
                if (moveToFailed) {
                    failedTasksCopy = [taskData?.data?.task, ...failedTasksCopy];
                }

                setAllTasks(
                    orderBy(
                        [
                            ...pendingTasksCopy,
                            ...activeTasksCopy,
                            ...completedTasksCopy,
                            ...failedTasksCopy,
                        ],
                        "createdAt",
                        ["desc"]
                    )
                );
            }
        }
    );

    useEffect(() => {
        setAllTasks(
            orderBy(
                [
                    ...(tasks?.activeTasks || []),
                    ...(tasks?.pendingTasks || []),
                    ...(tasks?.completedTasks || []),
                    ...(tasks?.failedTasks || []),
                ],
                "createdAt",
                ["desc"]
            )
        );
    }, [tasks]);

    return (
        <div className="">
            <div className="space-y-4 mb-4">
                <TextInput
                    name={"search"}
                    value={searchTask}
                    type={"text"}
                    inputPlaceholder={"Search tasks"}
                    inputClassName={"!h-[38px] pl-[0px] !bg-white text-sm !mb-0"}
                    onChange={(e) => handleSearch(e)}
                    onInput={handleDebouncedChange}
                    containerClassname={"!w-full max-sm:!w-full"}
                    leftIcon={
                        <i className="ri-search-line before:content-['\f0d1] text-slate-400 mt-[2px]"></i>
                    }
                    inputContainerClassname={
                        "!rounded-[4px] max-sm:!w-full !pl-[25px] !h-[40px] !bg-white !border !border-slate-200"
                    }
                />
                <div
                    className="flex items-center gap-2
                            "
                >
                    <Dropdown
                        name="filter"
                        value={""}
                        dropdownTriggerClassName={" "}
                        handleChange={(name, value) => {
                            handleTaskTypeChange(value);
                        }}
                        dropdown={
                            <div className="bg-white flex items-center justify-between h-10 gap-2 py-2.5 px-1.5 shadow-boxShadow-2 border border-slate-200 !rounded">
                                <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium">
                                    {taskType
                                        ? truncate(camelCaseToRegularCase(taskType), {
                                              length: 12,
                                          })
                                        : "All AI Agents"}
                                </span>

                                <div className="flex items-center space-x-2">
                                    {taskType && (
                                        <i
                                            onClick={(e) => {
                                                e?.stopPropagation();
                                                handleClearTaskTypeFilter();
                                            }}
                                            className="ri-close-circle-fill text-sm text-slate-500"
                                        ></i>
                                    )}
                                    <i className="ri-arrow-down-s-line text-lg text-gm-50"></i>
                                </div>
                            </div>
                        }
                        dropdownClassName={"!w-full !mb-10 !-mt-7"}
                        dropdownContainerClasses={`shadow-cardShadow-4 !left-0 !mt-0  !w-[159px] max-sm:!w-full  rounded !max-h-fit !p-0 !top-[105%]`}
                        wholeContainerClass={`w-full`}
                        dropdownItemsClasses="!bg-transparent"
                        dropdownOptions={filteredList?.map((agent) => {
                            return {
                                label: agentTypeRename(agent?.type),
                                value: agent?.type,
                                icon: (
                                    <i
                                        className={`ri-sparkling-fill text-base text-[${
                                            agent?.type === "orderProcessing"
                                                ? "#6562E5"
                                                : agent?.type === "marketingCampaign"
                                                ? "#FF4F01"
                                                : agent?.type === "EDIAssistant"
                                                ? "#00C6FA"
                                                : agent?.type === "InventoryManagement"
                                                ? "#FFB902"
                                                : "#6562E5"
                                        }]`}
                                    ></i>
                                ),
                            };
                        })}
                    />

                    <Dropdown
                        name="filter"
                        value={""}
                        dropdownTriggerClassName={" "}
                        handleChange={(name, value) => {
                            handleTaskStatusChange(value);
                        }}
                        dropdown={
                            <div className="bg-white flex items-center justify-between h-10 gap-2 py-2.5 px-1.5 shadow-boxShadow-2 border border-slate-200 !rounded">
                                <span className="text-[13px] text-slate-500 whitespace-nowrap font-rocGroteskMedium">
                                    Status:
                                </span>
                                <span className="text-[13px] whitespace-nowrap font-rocGroteskMedium capitalize">
                                    {truncate(taskStatus || "Status", {
                                        length: 8,
                                    })}
                                </span>

                                <div className="flex items-center space-x-0.5">
                                    {taskStatus && (
                                        <i
                                            onClick={(e) => {
                                                e?.stopPropagation();

                                                handleClearTaskStatusFilter();
                                            }}
                                            className="ri-close-circle-fill text-sm text-slate-500"
                                        ></i>
                                    )}
                                    <i className="ri-arrow-down-s-line text-lg text-gm-50"></i>
                                </div>
                            </div>
                        }
                        dropdownClassName={"!w-full !mb-10 !-mt-7"}
                        dropdownContainerClasses={`shadow-cardShadow-4 !right-0 !mt-0  !w-[159px] !max-w-[160px]  rounded !max-h-fit !p-0 !top-[105%]`}
                        wholeContainerClass={`w-full`}
                        dropdownItemsClasses="!bg-transparent "
                        dropdownOptions={[
                            {
                                label: "Pending",
                                value: "pending",
                            },
                            {
                                label: "In Progress",
                                value: "in progress",
                            },
                            {
                                label: "Completed",
                                value: "completed",
                            },
                            {
                                label: "Failed",
                                value: "failed",
                            },
                            {
                                label: "Attention Required",
                                value: "attention required",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="bg-[#F8FAFC] p-3 space-y-4 rounded-lg">
                {allTasks?.length > 0 ? (
                    allTasks?.map((task, idx) => {
                        const runningDurationinHrs = moment(task?.endDate || moment())?.diff(
                            task?.createdAt,
                            "hours"
                        );
                        const runningDurationinMins = moment(task?.endDate || moment())?.diff(
                            task?.createdAt,
                            "minutes"
                        );
                        const startDate = moment(task?.createdAt)?.format("MMM DD, YYYY hh:mm A");

                        return (
                            <TaskCard
                                key={task?._id}
                                title={`${task?.agent?.teamMateName || ""} - ${task?.taId || ""}`}
                                description={agentTypeRename(
                                    task?.taskType || task?.agent?.agentType
                                )}
                                time={
                                    runningDurationinHrs
                                        ? `${runningDurationinHrs} hour(s)`
                                        : `${runningDurationinMins} min(s)`
                                }
                                startDate={startDate}
                                userAttentionRequired={task?.status
                                    ?.toLowerCase()
                                    ?.startsWith("attention")}
                                avatarUrl={task?.agent?.avatar}
                                onClick={() =>
                                    task?.campaignId
                                        ? navigate(`/dashboard/campaign/${task?.campaignId}`, {
                                              state: { internalNavigation: true },
                                          })
                                        : navigate(`/dashboard/task/${task?._id}`, {
                                              state: { internalNavigation: true },
                                          })
                                }
                            />
                        );
                    })
                ) : (
                    <p className="text-slate-500 text-[13px] h-[200px] flex items-center justify-center text-center">
                        No tasks found
                    </p>
                )}
            </div>
        </div>
    );
};

export default FlowAllTask;
